import React, { useMemo } from 'react'
import classNames from 'classnames'
import { CustomDropdownItemOption, DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../SelectDropdown/SelectDropdown'
import { isSameTimezone } from '../../common/intlFormatters'
import { buildTimezoneOptions, getGmtOffsetLabel, getTimezoneLabel } from './utils'

interface TimezoneSelectorProps {
  initialSelectedTimezone: string
  onTimezoneSelected: (timezone: string | null) => void
  loading?: boolean
  disabled?: boolean
}

const US_TIMEZONES = new Set([
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Indiana/Indianapolis',
])

const SEPARATOR_OPTION: SelectOption = {
  id: 'timezone-separator',
  label: '──────────',
  displayLabel: <div className='w-100 border-top my-1' />,
  disabled: true,
  className: 'px-0',
}

const renderTimezoneLabel = (label: string) => {
  const match = label.match(/^(.*?)\s*(\(.*\))\s*$/)
  if (!match) {
    return <strong>{label}</strong>
  }

  const main = match[1]
  const suffix = match[2]
  return (
    <>
      <strong>{main}</strong> {suffix}
    </>
  )
}

const renderTimezoneOptionContent = (ianaTimezone: string) => {
  const label = getTimezoneLabel(ianaTimezone)
  const gmtOffsetLabel = getGmtOffsetLabel(ianaTimezone)

  return (
    <>
      <span className='text-nowrap'>{gmtOffsetLabel}</span>
      <span className='text-truncate'>{renderTimezoneLabel(label)}</span>
    </>
  )
}

const toTimezoneOption = (ianaTimezone: string): SelectOption => ({
  id: ianaTimezone,
  label: `${getGmtOffsetLabel(ianaTimezone)} ${getTimezoneLabel(ianaTimezone)}`,
  displayLabel: <span className='d-flex align-items-baseline gap-1 text-truncate'>{renderTimezoneOptionContent(ianaTimezone)}</span>,
})

let baseOptions: SelectOption[] | null = null

const getBaseOptions = (): SelectOption[] => {
  if (baseOptions) {
    return baseOptions
  }
  const entries = buildTimezoneOptions().map(toTimezoneOption)
  const sortedUs = entries.filter((e) => US_TIMEZONES.has(e.id)).sort((a, b) => a.label.localeCompare(b.label))
  const sortedRest = entries.filter((e) => !US_TIMEZONES.has(e.id)).sort((a, b) => a.label.localeCompare(b.label))
  baseOptions = [...sortedUs, SEPARATOR_OPTION, ...sortedRest]
  return baseOptions
}

const optionsForSelection = (selectedTimezone: string): SelectOption[] => {
  const base = getBaseOptions()
  if (!selectedTimezone || base.some((option) => option.id !== SEPARATOR_OPTION.id && isSameTimezone(option.id, selectedTimezone))) {
    return base
  }
  return [...base, toTimezoneOption(selectedTimezone)]
}

const TimezoneDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = ({ optionId, optionLabel, onClick, displayLabel, disabled, className }) => {
  const isSeparator = optionId === 'timezone-separator'

  if (isSeparator) {
    return <div className={classNames('px-0', className)}>{displayLabel ?? optionLabel}</div>
  }

  return (
    <CustomDropdownItemOption
      role='button'
      onClick={disabled ? () => {} : onClick}
      className={classNames('d-flex gap-2 align-items-center text-truncate paragraph-medium py-2', className, {
        'zocks-text-gray-500': disabled,
      })}
    >
      {displayLabel ?? optionLabel}
    </CustomDropdownItemOption>
  )
}

export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({ initialSelectedTimezone, onTimezoneSelected, loading = false, disabled = false }) => {
  const timezoneOptions = useMemo<SelectOption[]>((): SelectOption[] => optionsForSelection(initialSelectedTimezone), [initialSelectedTimezone])

  const initialSelectedOption = useMemo<SelectOption | null>(
    (): SelectOption | null =>
      initialSelectedTimezone ? (timezoneOptions.find((o) => o.id !== SEPARATOR_OPTION.id && isSameTimezone(o.id, initialSelectedTimezone)) ?? null) : null,
    [initialSelectedTimezone, timezoneOptions],
  )

  const handleOptionSelected = (option: SelectOption) => {
    if (option.disabled) {
      return
    }

    onTimezoneSelected(option.id)
  }

  return (
    <div>
      <SelectDropdown
        options={timezoneOptions}
        initialSelectedOption={initialSelectedOption}
        onOptionSelected={handleOptionSelected}
        placeholder='Select Timezone'
        DropDownItemOptionComponent={TimezoneDropdownItemOption}
        loading={loading}
        disabled={disabled}
        searchable
        hideClearButton={true}
      />
    </div>
  )
}
